"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { ArrowRight, BookOpen, Calculator, Star } from "lucide-react";
import posthog from "posthog-js";
import { buttonStyles } from "@/ui/components/ui/button";
import { getPeptideLinks } from "@/config/peptide-links";
import { formatPrice, isValidLocale, defaultLocale, type Locale } from "@/config/locale";
import { translatePeptideCategory } from "@/lib/peptide-translations";
import type { RecommendationResult } from "@/lib/quiz-scoring";
import type { ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";

interface Props {
	result: RecommendationResult;
	locale: string;
	channel: string;
	position: number;
	variant: "hero" | "alternative";
	pricePresentation?: ResolvedMoneyPresentation | null;
}

export function QuizRecommendationCard({
	result,
	locale,
	channel,
	position,
	variant,
	pricePresentation,
}: Props) {
	const t = useTranslations("quiz");
	const { peptide, match } = result;
	const productUrl = `/${locale}/${channel}/products/${peptide.slug}`;
	const links = getPeptideLinks(peptide.slug);

	const loc: Locale = isValidLocale(locale) ? locale : defaultLocale;
	const rawLabel =
		peptide.priceFrom && peptide.priceFrom.amount > 0
			? formatPrice(peptide.priceFrom.amount, peptide.priceFrom.currency, loc)
			: null;
	const resolvedLabel = pricePresentation?.primaryLabel ?? rawLabel;
	const priceLabel = resolvedLabel ? t("priceFrom", { price: resolvedLabel }) : null;

	const onClick = () => {
		posthog.capture("quiz_result_cta_clicked", {
			peptide_slug: peptide.slug,
			position,
		});
	};

	if (variant === "hero") {
		return (
			<div className="to-primary-hero/80 rounded-2xl bg-gradient-to-br from-primary-hero p-6 text-primary-hero-foreground shadow-lg sm:p-8">
				<div className="text-primary-hero-foreground/80 mb-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider">
					<Star className="h-3.5 w-3.5" aria-hidden />
					{t("results.topMatchLabel")} · {match.overallPercent}% {t("match")}
				</div>
				<h3 className="text-2xl font-bold tracking-tight sm:text-3xl">{peptide.name}</h3>
				<div className="text-primary-hero-foreground/80 mt-2 flex flex-wrap gap-2 text-sm">
					{peptide.categories.slice(0, 3).map((c) => (
						<span key={c}>{translatePeptideCategory(c, locale)}</span>
					))}
				</div>
				<div className="text-primary-hero-foreground/70 mt-4 flex flex-wrap items-baseline gap-3 text-xs sm:text-sm">
					{priceLabel && (
						<span className="text-lg font-bold tabular-nums text-primary-hero-foreground sm:text-xl">
							{priceLabel}
						</span>
					)}
					{peptide.dosingFrequency && <span>{peptide.dosingFrequency}</span>}
					{peptide.timing && <span>· {peptide.timing}</span>}
				</div>
				<div className="mt-6 flex flex-wrap items-center gap-4">
					<Link
						href={productUrl}
						onClick={onClick}
						aria-label={`${t("shopProduct")} ${peptide.name}`}
						className={buttonStyles({
							variant: "default",
							className: "hover:bg-background/90 bg-background text-foreground",
						})}
					>
						{t("shopProduct")}
						<ArrowRight className="ml-2 h-4 w-4" aria-hidden />
					</Link>
					{links?.guideSlug && (
						<Link
							href={`/${locale}/${channel}/guides/${links.guideSlug}`}
							className="text-primary-hero-foreground/80 inline-flex items-center gap-1.5 text-sm hover:text-primary-hero-foreground"
						>
							<BookOpen className="h-3.5 w-3.5" aria-hidden />
							{t("links.guide")}
						</Link>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className="hover:border-primary/40 flex h-full flex-col rounded-xl border border-border bg-card p-4 shadow-xs transition-all hover:shadow-md">
			<div className="flex items-baseline justify-between gap-2">
				<h4 className="text-base font-semibold">{peptide.name}</h4>
				<span className="text-xs font-semibold tabular-nums text-primary">{match.overallPercent}%</span>
			</div>
			<div className="mt-1 text-xs text-muted-foreground">
				{peptide.categories
					.slice(0, 2)
					.map((c) => translatePeptideCategory(c, locale))
					.join(" · ")}
			</div>
			{priceLabel && (
				<div className="mt-2 text-sm font-semibold tabular-nums text-foreground">{priceLabel}</div>
			)}
			{peptide.dosingFrequency && (
				<div className="mt-1 text-xs text-muted-foreground">{peptide.dosingFrequency}</div>
			)}
			<Link
				href={productUrl}
				onClick={onClick}
				aria-label={`${t("viewProduct")} ${peptide.name}`}
				className="mt-auto inline-flex items-center gap-1 pt-3 text-sm font-semibold text-primary hover:underline"
			>
				{t("viewProduct")}
				<ArrowRight className="h-3.5 w-3.5" aria-hidden />
			</Link>
			{links && (links.guideSlug || links.calculatorSlug) && (
				<div className="mt-2 flex gap-3 text-xs text-muted-foreground">
					{links.guideSlug && (
						<Link
							href={`/${locale}/${channel}/guides/${links.guideSlug}`}
							className="inline-flex items-center gap-1 hover:text-primary"
						>
							<BookOpen className="h-3 w-3" aria-hidden />
							{t("links.guide")}
						</Link>
					)}
					{links.calculatorSlug && (
						<Link
							href={`/${locale}/${channel}/peptide-calculator/${links.calculatorSlug}`}
							className="inline-flex items-center gap-1 hover:text-primary"
						>
							<Calculator className="h-3 w-3" aria-hidden />
							{t("links.calculator")}
						</Link>
					)}
				</div>
			)}
		</div>
	);
}
